import { WhatsAppConnectorStatus } from './business-service';

export type ConnectorPlatform = 'mac-arm64' | 'mac-x64' | 'win' | 'linux-appimage' | 'linux-deb';

export const connectorTermsUrl = 'https://organizabot.com/terms';

const downloadUrlKey = 'REACT_APP_CONNECTOR_DOWNLOAD_URL';

export function connectorDownloads(): { platform: ConnectorPlatform; label: string; url: string }[] {
    const downloadUrl = process.env[downloadUrlKey];
    if (!downloadUrl) {
        throw new Error(`No value found for environment value "${downloadUrlKey}"`);
    }
    return [
        {
            platform: 'mac-arm64',
            label: 'macOS (Apple Silicon: M1 ou mais novo)',
            url: `${downloadUrl}/OrganizaBot-Conector-mac-arm64.dmg`,
        },
        {
            platform: 'mac-x64',
            label: 'macOS (processador Intel)',
            url: `${downloadUrl}/OrganizaBot-Conector-mac-x64.dmg`,
        },
        { platform: 'win', label: 'Windows', url: `${downloadUrl}/OrganizaBot-Conector-win-x64.exe` },
        {
            platform: 'linux-appimage',
            label: 'Linux (AppImage)',
            url: `${downloadUrl}/OrganizaBot-Conector-linux-x86_64.AppImage`,
        },
        { platform: 'linux-deb', label: 'Linux (.deb)', url: `${downloadUrl}/OrganizaBot-Conector-linux-amd64.deb` },
    ];
}

/** What the browser tells about the device; `architecture` comes from Chromium's client hints when available. */
export type ConnectorDevice = { userAgent: string; maxTouchPoints: number; architecture: string | null };

/** Phones and tablets, including iPads that report a Mac user agent. */
export function isHandheldDevice(device: ConnectorDevice): boolean {
    if (/iPhone|iPad|iPod|Android/.test(device.userAgent)) return true;
    return /Macintosh/.test(device.userAgent) && device.maxTouchPoints > 1;
}

/** Best guess from the browser; Macs report Intel even on Apple Silicon, so without client hints those get the Apple Silicon build. */
export function detectConnectorPlatform(device: ConnectorDevice): ConnectorPlatform | null {
    if (isHandheldDevice(device)) return null;
    if (/Windows/.test(device.userAgent)) return 'win';
    if (/Macintosh|Mac OS X/.test(device.userAgent)) return device.architecture === 'x86' ? 'mac-x64' : 'mac-arm64';
    if (/Linux/.test(device.userAgent)) return 'linux-appimage';
    return null;
}

export function readConnectorDevice(): ConnectorDevice {
    return { userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints, architecture: null };
}

export async function readConnectorArchitecture(): Promise<string | null> {
    if (!navigator.userAgentData) return null;
    try {
        const { architecture } = await navigator.userAgentData.getHighEntropyValues(['architecture']);
        return architecture ?? null;
    } catch {
        return null;
    }
}

export type ConnectorConnectionState =
    | 'invalidated'
    | 'awaiting_app'
    | 'offline'
    | 'pairing'
    | 'reconnecting'
    | 'connected';

/** The state of a number linked through the connector, from the most to the least severe problem. */
export function connectorConnectionState(params: {
    invalidated: boolean;
    status: WhatsAppConnectorStatus | null;
}): ConnectorConnectionState {
    const { invalidated, status } = params;
    if (invalidated) return 'invalidated';
    if (status === null) return 'offline';
    // A computer that was never seen was just approved and its app hasn't come up yet: nothing failed.
    if (!status.online) return status.lastSeenAt === null ? 'awaiting_app' : 'offline';
    if (status.whatsapp === 'pairing' || status.whatsapp === 'logged_out') return 'pairing';
    if (status.whatsapp !== 'connected') return 'reconnecting';
    return 'connected';
}

const offlineWarningDelayMs = 15 * 60_000;

/** Short drops (restarts, flaky Wi-Fi) don't warrant a warning; same grace the server applies before warning the owner. */
export function isConnectorOfflineForLong(status: WhatsAppConnectorStatus, now: number): boolean {
    if (status.online) return false;
    // Never seen: just approved, waiting for the app, not an outage.
    if (status.lastSeenAt === null) return false;
    return now - Date.parse(status.lastSeenAt) >= offlineWarningDelayMs;
}

const pendingCodeKey = 'pendingConnectorCode';

/**
 * The connector opens `/connector?code=XXXX-XXXX` for the owner to approve. The code is kept
 * across the login screen and the panel's own route rewriting until it is handled.
 */
export function capturePendingConnectorCode(location: { pathname: string; search: string }): void {
    if (location.pathname.replace(/\/+$/, '') !== '/connector') return;
    const code = new URLSearchParams(location.search).get('code');
    if (code) sessionStorage.setItem(pendingCodeKey, code);
}

export function readPendingConnectorCode(): string | null {
    return sessionStorage.getItem(pendingCodeKey);
}

export function clearPendingConnectorCode(): void {
    sessionStorage.removeItem(pendingCodeKey);
}
